/**
 * Shown instead of the app when required env vars are missing.
 *
 * This is the first thing a fresh Render deploy hits if the environment was
 * not filled in, so it names the exact variables and where they come from
 * rather than leaving a blank page and a console error.
 */
import { AlertTriangle } from "lucide-react";

const SOURCES: Record<string, string> = {
  VITE_CONVEX_URL:
    "Convex dashboard → Settings → URL & Deploy Key, or the URL printed by `npx convex dev`.",
  VITE_CLERK_PUBLISHABLE_KEY:
    "Clerk dashboard → API Keys → Publishable key (starts with pk_).",
};

export function ConfigError({ missing }: { missing: string[] }) {
  return (
    <div className="min-h-screen bg-[#0A0B0F] text-slate-100 flex items-center justify-center p-6">
      <div className="max-w-lg w-full rounded-xl border border-amber-500/30 bg-amber-500/5 p-6">
        <div className="flex items-center gap-3 mb-4">
          <AlertTriangle className="w-6 h-6 text-amber-400 shrink-0" />
          <h1 className="text-lg font-semibold">Dashbase is not configured</h1>
        </div>

        <p className="text-sm text-slate-400 mb-4">
          {missing.length === 1
            ? "This environment variable is missing at build time:"
            : "These environment variables are missing at build time:"}
        </p>

        <ul className="space-y-3 mb-5">
          {missing.map((name) => (
            <li key={name} className="text-sm">
              <code className="font-mono text-amber-300">{name}</code>
              <p className="text-slate-400 mt-1">{SOURCES[name]}</p>
            </li>
          ))}
        </ul>

        <p className="text-xs text-slate-500">
          Locally these go in <code className="font-mono">.env.local</code>. On
          Render they are set per service under Environment, and the site must
          be rebuilt afterwards &mdash; Vite inlines them at build time, so a
          restart alone will not pick them up.
        </p>
      </div>
    </div>
  );
}
